import { Component, type ReactNode } from "react";

export interface ChunkErrorBoundaryProps {
  /** Content to protect. */
  children: ReactNode;
  /** Optional fallback UI. Defaults to a centered error message with a Retry button. */
  fallback?: ReactNode;
}

interface ChunkErrorBoundaryState {
  error: Error | null;
}

/**
 * Error boundary for lazy-loaded chunks.
 *
 * Catches errors thrown during dynamic `import()` — typically network failures
 * or stale chunk references after a deployment. Displays a retry button so the
 * user can attempt to re-fetch the chunk without a full page reload.
 */
export class ChunkErrorBoundary extends Component<
  ChunkErrorBoundaryProps,
  ChunkErrorBoundaryState
> {
  state: ChunkErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ChunkErrorBoundaryState {
    return { error };
  }

  private handleRetry = () => {
    this.setState({ error: null });
  };

  render() {
    if (this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex flex-col items-center justify-center gap-3 py-12">
          <p className="text-sm text-danger">Failed to load this content.</p>
          <button
            type="button"
            onClick={this.handleRetry}
            className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-fg transition-colors hover:bg-primary/90"
          >
            Retry
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
